import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useChecklists } from '../../contexts/ChecklistContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { formatDate, getTodayString, toLocalISODate } from '../../utils/dateUtils';
import { validateChecklistForm } from '../../utils/validationUtils';
import { getReorderedOrder } from '../../utils/reorderUtils';
import { isActiveOn, REPEAT_UNITS, REPEAT_INTERVAL_CHOICES } from '../../utils/checklistCycle';
import { BarChart, Bar, Cell, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART_GRID, CHART_SLOTS } from '../../utils/chartPalette';

const emptyForm = {
  title: '', description: '', date: getTodayString(), reminderType: 'once',
  repeatEvery: 1, repeatUnit: 'day', reminderTime: '', repeatIntervalMinutes: '', tasks: [],
};

// "Every 2 weeks" in one word per unit, so a translator never has to assemble a sentence
// out of fragments. Singular keys exist because "Every 1 days" is how software tells you
// nobody read its output.
function recurrenceLabel(checklist, t) {
  if (checklist.reminderType === 'daily') return t('daily');
  if (checklist.reminderType !== 'every') return t('oneTime');
  const count = Number(checklist.repeatEvery) || 1;
  const unit = REPEAT_UNITS.includes(checklist.repeatUnit) ? checklist.repeatUnit : 'day';
  return t(`every.${unit}${count === 1 ? '' : 's'}`, { count });
}

function repeatNudgeLabel(minutes, t) {
  if (!minutes) return '';
  return minutes % 60 === 0 ? t('repeatEveryHours', { count: minutes / 60 }) : t('repeatEveryMinutes', { count: minutes });
}

function shiftDate(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + days);
  return toLocalISODate(date);
}

// Deliberately a workload chart, not a "completion over time" chart. A checklist stores
// one tasks array with a current completed flag and no history, and a daily list reuses
// the same array every day - so there is no honest way to say how much was finished last
// Tuesday. Counting how many tasks each day carries is something the data does support.
function WorkloadTooltip({ active, payload, t }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <strong>{row.label}</strong>
      <span>{t('charts.tasksOnDay', { count: row.tasks })}</span>
      {row.isSelected ? <span>{t('charts.selectedDay')}</span> : null}
    </div>
  );
}

function ChecklistCard({
  checklist, doneCount, totalCount, pct, allDone, t, handleToggle, handleEdit, handleDelete, sortable,
}) {
  const dragStyle = sortable ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition } : undefined;
  const isRepeatingList = checklist.reminderType === 'daily' || checklist.reminderType === 'every';
  const nudgeLabel = repeatNudgeLabel(checklist.repeatIntervalMinutes, t);
  return (
    <article
      ref={sortable?.setNodeRef}
      style={dragStyle}
      {...(sortable?.attributes || {})}
      {...(sortable?.listeners || {})}
      className={`cl-card${allDone ? ' is-complete' : ''}${sortable ? ' draggable' : ''}${sortable?.isDragging ? ' dragging' : ''}`}
    >
      <div className="cl-card-top">
        <h3 title={checklist.title}>{checklist.title}</h3>
        <span className="cl-frac">{allDone ? '✓ ' : ''}{doneCount}/{totalCount}</span>
      </div>

      <div className="progress-bar" aria-label={t('progress')}><div style={{ width: `${pct}%` }} /></div>

      <div className="cl-tag-row">
        <span className={`reminder-pill ${isRepeatingList ? 'daily' : 'once'}`}>
          {recurrenceLabel(checklist, t)}
          {/* A reminder the user can't see the time of is one they can't trust. */}
          {checklist.reminderTime ? ` · ${checklist.reminderTime}` : ''}
          {nudgeLabel ? ` · ${nudgeLabel}` : ''}
        </span>
        {/* A repeating list's date is when it started, not when it is due - saying so
            stops it reading as an overdue one-off. */}
        <span className="cl-when">{isRepeatingList ? t('since', { date: formatDate(checklist.date) }) : formatDate(checklist.date)}</span>
      </div>

      {checklist.description && <p className="cl-description">{checklist.description}</p>}

      <ul className="task-list">
        {checklist.tasks.map((task) => (
          <li key={task.id}>
            <label>
              <input type="checkbox" checked={task.completed} onChange={() => handleToggle(checklist.id, task.id)} />
              <span className={task.completed ? 'done' : ''}>{task.text}</span>
            </label>
          </li>
        ))}
        {checklist.tasks.length === 0 && <li className="task-list-empty">{t('noTasksYet')}</li>}
      </ul>

      <div className="cl-actions">
        <button type="button" onClick={() => handleEdit(checklist)} className="cl-act-btn">{t('edit')}</button>
        <button type="button" onClick={() => handleDelete(checklist)} className="cl-act-btn danger">{t('delete')}</button>
      </div>
    </article>
  );
}

function SortableChecklistCard(props) {
  const sortable = useSortable({ id: props.checklist.id });
  return <ChecklistCard {...props} sortable={sortable} />;
}

function ChecklistDashboard() {
  const { t } = useTranslation('checklist');
  const { checklists, addChecklist, updateChecklist, toggleTask, deleteChecklist, reorderChecklist } = useChecklists();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [form, setForm] = useState(emptyForm);
  const [newTaskText, setNewTaskText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [errors, setErrors] = useState({});

  const today = getTodayString();
  const isToday = selectedDate === today;

  const visibleChecklists = useMemo(
    () => checklists.filter((checklist) => isActiveOn(checklist, selectedDate)),
    [checklists, selectedDate],
  );

  // The headline counts the day you are actually looking at. Reporting today's numbers
  // above yesterday's list is how a progress bar starts lying to people.
  const doneTasks = visibleChecklists.reduce((count, checklist) => count + checklist.tasks.filter((task) => task.completed).length, 0);
  const totalTasks = visibleChecklists.reduce((count, checklist) => count + checklist.tasks.length, 0);
  const completionRate = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const remaining = totalTasks - doneTasks;
  const dayIsClear = totalTasks > 0 && remaining === 0;

  const headline = () => {
    if (totalTasks === 0) return t('hero.noTasks');
    if (remaining === 0) return t('hero.allDone');
    if (remaining === 1) return t('hero.oneLeft');
    return t('hero.remaining', { count: remaining });
  };

  // Two weeks centred on the day you are looking at: enough to see a pile-up coming
  // without turning into a calendar.
  const workload = useMemo(() => {
    const days = [];
    for (let offset = -6; offset <= 7; offset += 1) {
      const date = shiftDate(selectedDate, offset);
      const tasks = checklists
        .filter((checklist) => isActiveOn(checklist, date))
        .reduce((count, checklist) => count + checklist.tasks.length, 0);
      days.push({
        date,
        label: formatDate(date),
        // Day of month only - fourteen full dates will not fit across this axis.
        tick: date.slice(8, 10),
        tasks,
        isSelected: date === selectedDate,
      });
    }
    return days;
  }, [checklists, selectedDate]);
  const hasWorkload = workload.some((day) => day.tasks > 0);

  const openAddChecklist = () => {
    setEditingId(null);
    setForm({ ...emptyForm, date: selectedDate });
    setNewTaskText('');
    setErrors({});
    setShowChecklistForm(true);
  };

  const closeChecklistForm = () => {
    setShowChecklistForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setNewTaskText('');
    setErrors({});
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const validation = validateChecklistForm(form);
    if (Object.keys(validation).length) {
      setErrors(validation);
      return;
    }

    const payload = {
      ...form,
      reminderTime: form.reminderTime || null,
      repeatEvery: Number(form.repeatEvery) || 1,
      repeatIntervalMinutes: form.repeatIntervalMinutes ? Number(form.repeatIntervalMinutes) : null,
    };

    if (editingId) {
      updateChecklist({ ...payload, id: editingId });
      addToast(t('toast.checklistUpdated'), 'success');
    } else {
      addChecklist(payload);
      addToast(t('toast.checklistAdded'), 'success');
    }
    closeChecklistForm();
  };

  const handleEdit = (checklist) => {
    setEditingId(checklist.id);
    setForm({
      title: checklist.title,
      description: checklist.description || '',
      date: checklist.date,
      reminderType: checklist.reminderType || 'once',
      repeatEvery: checklist.repeatEvery || 1,
      repeatUnit: checklist.repeatUnit || 'day',
      reminderTime: checklist.reminderTime || '',
      repeatIntervalMinutes: checklist.repeatIntervalMinutes || '',
      tasks: checklist.tasks,
    });
    setNewTaskText('');
    setErrors({});
    setShowChecklistForm(true);
  };

  const addTaskToForm = () => {
    const trimmed = newTaskText.trim();
    if (!trimmed) return;
    setForm({ ...form, tasks: [...form.tasks, { id: Date.now(), text: trimmed, completed: false }] });
    setNewTaskText('');
  };

  const removeTaskFromForm = (index) => {
    setForm({ ...form, tasks: form.tasks.filter((_, i) => i !== index) });
  };

  const updateTaskTextInForm = (index, text) => {
    setForm({ ...form, tasks: form.tasks.map((task, i) => (i === index ? { ...task, text } : task)) });
  };

  const handleToggle = (checklistId, taskId) => {
    toggleTask(checklistId, taskId);
    addToast(t('toast.taskUpdated'), 'success');
  };

  const handleDelete = async (checklist) => {
    const confirmed = await confirm(t('confirmDelete', { title: checklist.title }));
    if (!confirmed) return;
    deleteChecklist(checklist.id);
    addToast(t('toast.checklistDeleted'), 'success');
  };

  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { delay: 200, tolerance: 5 } }));

  const handleChecklistDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = visibleChecklists.findIndex((checklist) => checklist.id === active.id);
    const toIndex = visibleChecklists.findIndex((checklist) => checklist.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    reorderChecklist(active.id, getReorderedOrder(visibleChecklists, fromIndex, toIndex));
  };

  useEffect(() => {
    if (!showChecklistForm) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeChecklistForm();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showChecklistForm]);

  return (
    <section className="page-card">
      <div className="page-header checklist-header">
        <div>
          <p className="eyebrow">{t('eyebrow')}</p>
          <h1 className="display-title">{t('heading')}</h1>
        </div>
        <div className="page-actions">
          <button onClick={openAddChecklist} className="primary-btn standout-btn">
            <span aria-hidden="true">+</span> {t('addChecklist')}
          </button>
        </div>
      </div>

      {/* Today's progress and the days around it read together - what is left now, and
          what is coming - so they share one row rather than two full-width strips. */}
      <div className={`dash-lead${hasWorkload ? '' : ' is-single'}`}>
        <section className={`cl-hero${dayIsClear ? ' is-clear' : ''}`}>
          <div className="cl-hero-top">
            <div>
              <p className="cl-hero-eyebrow">{isToday ? t('hero.today') : t('hero.otherDay')}</p>
              <h3 className="cl-hero-headline">{headline()}</h3>
            </div>
            <div className="cl-date-nav">
              <button type="button" className="cl-date-step" aria-label={t('prevDay')} onClick={() => setSelectedDate(shiftDate(selectedDate, -1))}>‹</button>
              <input
                type="date"
                className="cl-date-input"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value || today)}
              />
              <button type="button" className="cl-date-step" aria-label={t('nextDay')} onClick={() => setSelectedDate(shiftDate(selectedDate, 1))}>›</button>
              {!isToday && <button type="button" className="text-btn" onClick={() => setSelectedDate(today)}>{t('today')}</button>}
            </div>
          </div>
          <div className="cl-hero-progress">
            <div className="cl-hero-figures">
              <span className="cl-hero-count">
                <strong>{doneTasks}</strong>
                <span>/ {totalTasks} {t('hero.tasks')}</span>
              </span>
              <span className="cl-hero-pct">{completionRate}%</span>
            </div>
            <div className="progress-bar" aria-label={t('progress')}><div style={{ width: `${completionRate}%` }} /></div>
          </div>
        </section>

        {hasWorkload && (
          <section className="dash-chart-card cl-workload-card">
            <header className="dash-chart-head">
              <h2>{t('charts.workloadTitle')}</h2>
              <span className="dash-chart-sub">{t('charts.workloadSub')}</span>
            </header>
            <div className="dash-chart-body">
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={workload} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="tick" tickLine={false} axisLine={{ stroke: CHART_GRID }} tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={30} tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }} />
                  <Tooltip cursor={{ fill: 'currentColor', opacity: 0.06 }} content={<WorkloadTooltip t={t} />} />
                  <Bar dataKey="tasks" radius={[4, 4, 0, 0]} maxBarSize={26}>
                    {workload.map((day) => (
                      // The day in view is the one bar that is not just data - it is where
                      // the reader already is, so it keeps full colour and the rest recede.
                      <Cell
                        key={day.date}
                        fill={CHART_SLOTS[0]}
                        fillOpacity={day.isSelected ? 1 : 0.42}
                        cursor="pointer"
                        onClick={() => setSelectedDate(day.date)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </div>

      <div className="checklist-grid">
        {checklists.length === 0 && (
          <div className="cl-empty">
            <span className="cl-empty-icon" aria-hidden="true">📋</span>
            <h3>{t('empty.title')}</h3>
            <p>{t('empty.body')}</p>
            <div className="cl-empty-actions">
              <button type="button" className="primary-btn standout-btn" onClick={openAddChecklist}>{t('empty.cta')}</button>
            </div>
          </div>
        )}
        {checklists.length > 0 && visibleChecklists.length === 0 && (
          <div className="cl-empty">
            <span className="cl-empty-icon" aria-hidden="true">🗓️</span>
            <h3>{t('empty.noneTitle')}</h3>
            <p>{t('empty.noneBody')}</p>
            <div className="cl-empty-actions">
              <button type="button" className="text-btn" onClick={openAddChecklist}>{t('empty.addForDay')}</button>
              {!isToday && <button type="button" className="text-btn" onClick={() => setSelectedDate(today)}>{t('empty.backToToday')}</button>}
            </div>
          </div>
        )}
        <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleChecklistDragEnd}>
          <SortableContext items={visibleChecklists.map((checklist) => checklist.id)} strategy={verticalListSortingStrategy}>
            {visibleChecklists.map((checklist) => {
              const doneCount = checklist.tasks.filter((task) => task.completed).length;
              const totalCount = checklist.tasks.length;
              const pct = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;
              const allDone = totalCount > 0 && doneCount === totalCount;
              return (
                <SortableChecklistCard
                  key={checklist.id}
                  checklist={checklist}
                  doneCount={doneCount}
                  totalCount={totalCount}
                  pct={pct}
                  allDone={allDone}
                  t={t}
                  handleToggle={handleToggle}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      {showChecklistForm && (
        <div className="modal-overlay" onClick={closeChecklistForm}>
          <form onSubmit={handleSubmit} className="card form-card modal-content" onClick={(event) => event.stopPropagation()}>
            <h3>{editingId ? t('form.editTitle') : t('form.addTitle')}</h3>
            <label>
              {t('form.name')}
              <input autoFocus value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
              {errors.title && <span className="error-text">{t('validation.titleRequired')}</span>}
            </label>
            <label>
              {t('form.reminder')}
              <div className="segmented-toggle">
                <button type="button" className={form.reminderType === 'daily' ? 'active' : ''} onClick={() => setForm({ ...form, reminderType: 'daily' })}>{t('form.dailyReminder')}</button>
                <button type="button" className={form.reminderType === 'every' ? 'active' : ''} onClick={() => setForm({ ...form, reminderType: 'every' })}>{t('form.everyReminder')}</button>
                <button type="button" className={form.reminderType === 'once' ? 'active' : ''} onClick={() => setForm({ ...form, reminderType: 'once' })}>{t('form.oneTimeReminder')}</button>
              </div>
            </label>
            {form.reminderType === 'every' && (
              <div className="inline-fields">
                <label>
                  {t('form.repeatEvery')}
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={form.repeatEvery}
                    onChange={(event) => setForm({ ...form, repeatEvery: event.target.value })}
                  />
                </label>
                <label>
                  {t('form.repeatUnit')}
                  <select value={form.repeatUnit} onChange={(event) => setForm({ ...form, repeatUnit: event.target.value })}>
                    {REPEAT_UNITS.map((unit) => <option key={unit} value={unit}>{t(`units.${unit}`)}</option>)}
                  </select>
                  {/* The cycle is counted from the starting date below, so that field is
                      what decides which days a "every 3 days" list lands on. */}
                  <span className="field-hint">{t('form.repeatUnitHint')}</span>
                </label>
              </div>
            )}
            <label>
              {t('form.description')}
              <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            <div className="inline-fields">
              <label>
                {form.reminderType === 'once' ? t('form.date') : t('form.startingDate')}
                <input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
                {errors.date && <span className="error-text">{t('validation.dateRequired')}</span>}
              </label>
              <label>
                {t('form.time')}
                <input
                  type="time"
                  value={form.reminderTime}
                  onChange={(event) => setForm({ ...form, reminderTime: event.target.value })}
                />
                <span className="field-hint">{t('form.timeHint')}</span>
              </label>
            </div>
            <label>
              {/* A second reminder for the lists one nudge never gets done. Off by
                  default: an app that pings you every hour by default is one you mute. */}
              {t('form.repeatReminder')}
              <select
                value={form.repeatIntervalMinutes}
                onChange={(event) => setForm({ ...form, repeatIntervalMinutes: event.target.value })}
              >
                <option value="">{t('form.repeatReminderOff')}</option>
                {REPEAT_INTERVAL_CHOICES.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes % 60 === 0 ? t('repeatEveryHours', { count: minutes / 60 }) : t('repeatEveryMinutes', { count: minutes })}
                  </option>
                ))}
              </select>
              <span className="field-hint">{t('form.repeatReminderHint')}</span>
            </label>
            <label>
              {t('form.tasks')}
              <div className="task-editor">
                {form.tasks.length === 0 && <span className="task-editor-empty">{t('form.tasksHint')}</span>}
                {form.tasks.map((task, index) => (
                  <div key={task.id} className="task-editor-row">
                    <input value={task.text} onChange={(event) => updateTaskTextInForm(index, event.target.value)} />
                    <button type="button" className="text-btn danger icon-btn" aria-label={t('form.removeTask')} onClick={() => removeTaskFromForm(index)}>✕</button>
                  </div>
                ))}
                <div className="task-editor-row">
                  <input placeholder={t('form.newTaskPlaceholder')} value={newTaskText} onChange={(event) => setNewTaskText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTaskToForm(); } }} />
                  <button type="button" className="text-btn" onClick={addTaskToForm}>{t('form.add')}</button>
                </div>
              </div>
            </label>
            <div className="modal-actions">
              <button type="button" className="text-btn" onClick={closeChecklistForm}>{t('form.cancel')}</button>
              <button className="primary-btn standout-btn" type="submit">{editingId ? t('form.saveChanges') : t('form.createChecklist')}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default ChecklistDashboard;
